// frontend/src/features/bapb/cadApi.js
// Unified client for CAD value refresh (auto-detects CAD server-side via cover_meta)

import axios from "axios";

function cleanBase(u) {
  const s = (u || "").trim();
  if (!s) return "";
  return s.endsWith("/") ? s.slice(0, -1) : s;
}

const ENV_BASE =
  (typeof window !== "undefined" && window.API_BASE) ||
  process.env.NEXT_PUBLIC_API_BASE ||
  process.env.REACT_APP_API_BASE ||
  "";

// Build candidate URLs that work in both dev and prod setups
function candidateUrls(path) {
  const base = cleanBase(ENV_BASE);
  const urls = [];
  // Absolute (if API base is provided)
  if (base) {
    urls.push(`${base}${path}`);
    urls.push(`${base}/api${path}`); // proxy setups that only forward /api/*
  }
  // Relative (same-origin)
  urls.push(path);
  urls.push(`/api${path}`); // same-origin proxy for /api/*
  return Array.from(new Set(urls));
}

async function tryGet(path) {
  const paths = candidateUrls(path);
  let lastErr;

  for (const url of paths) {
    try {
      const res = await axios.get(url, { withCredentials: true });
      return res.data;
    } catch (err) {
      const status = err?.response?.status;

      // If it's a non-404 server error, bubble it up now (don't try other URLs)
      if (status !== 404 && status !== 0 && status !== undefined) {
        throw err;
      }

      lastErr = err;
      continue;
    }
  }

  throw lastErr || new Error(`GET ${path} failed: no reachable endpoint`);
}

/**
 * GET /cad/supported
 * Returns: { supported: string[] }
 */
export async function getSupportedCads() {
  const data = await tryGet("/cad/supported");
  const list = data?.supported;
  return Array.isArray(list) ? list : [];
}

/**
 * POST /cad/refresh
 *
 * @param {Object} params
 * @param {number} params.year
 * @param {string[]} params.accounts
 * @param {Object} [params.cover_meta]
 * @param {string} [params.cad_key]
 * @param {number} [params.packet_id]
 * @param {string} [params.section_id]
 * @param {string} [params.section_type]
 */
export async function refreshValues(params) {
  const paths = candidateUrls("/cad/refresh");
  let lastErr;

  for (const url of paths) {
    try {
      const res = await axios.post(url, params, { withCredentials: true });
      return res.data;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      // If server sent a structured detail object, format it nicely.
      if (detail && typeof detail === "object" && (detail.message || detail.supported)) {
        const supportedList =
          Array.isArray(detail.supported) && detail.supported.length
            ? `\n\nSupported CADs:\n- ${detail.supported.join("\n- ")}`
            : "";
        throw new Error(`${detail.message || "Value Refreshing not supported."}${supportedList}`);
      }

      // If it's a non-404 server error, bubble it up now (don't try other URLs)
      if (status !== 404 && status !== 0 && status !== undefined) {
        if (typeof detail === "string") throw new Error(detail);
        throw err;
      }

      // Otherwise try the next candidate URL
      lastErr = err;
      continue;
    }
  }

  // If we ran out of candidates, surface the last error
  if (lastErr?.response?.data?.detail) {
    throw new Error(lastErr.response.data.detail);
  }
  throw lastErr || new Error("CAD refresh failed: no reachable endpoint");
}

/**
 * Refresh values for a potentially comma-separated multi-parcel account string.
 * Splits on commas, fetches all parcels, and sums numeric values.
 * Returns a single "merged" result that looks like a single-account response.
 */
export async function refreshMultiParcel({ year, accountStr, cover_meta }) {
  const accounts = (accountStr || "")
    .split(",")
    .map((a) => a.trim())
    .filter(Boolean);
  if (!accounts.length) return { results: [], warnings: [] };

  const resp = await refreshValues({ year, accounts, cover_meta });
  const rows = resp?.results || [];

  if (accounts.length === 1) {
    // Single parcel — return as-is
    return { merged: rows[0] || null, rows, warnings: [] };
  }

  // Multi-parcel: sum values across all successful results
  let land = 0, improvement = 0, totalMarket = 0, marketValue = 0;
  let anyOk = false;
  const warnings = [];

  for (const r of rows) {
    if (r.ok) {
      anyOk = true;
      land += Number(r.raw?.land || 0);
      improvement += Number(r.raw?.improvement || 0);
      totalMarket += Number(r.raw?.total_market || r.market_value || 0);
      marketValue += Number(r.market_value || r.raw?.total_market || 0);
    } else {
      warnings.push(`${r.account}: ${r.error || "no data"}`);
    }
  }

  const merged = {
    account: accountStr,
    ok: anyOk,
    market_value: marketValue,
    raw: { land, improvement, total_market: totalMarket },
    error: anyOk ? null : "All parcels failed",
  };

  return { merged, rows, warnings };
}

/**
 * Convenience helper for section calls.
 */
export async function refreshForSection({
  packetId,
  sectionId,
  sectionType,
  year,
  accounts,
  coverMeta,
}) {
  return refreshValues({
    packet_id: packetId,
    section_id: sectionId,
    section_type: sectionType,
    year,
    accounts,
    cover_meta: coverMeta,
  });
}